/**
 * SPEC-staging-hardening §5.7 — the live-ingest switch's stored shape and pure
 * helpers, shared by the server reader, the Admin API and the Admin card.
 *
 *   off    — webhooks are acknowledged and dropped
 *   ingest — deliveries are recorded; runActualExtraction and the polling
 *            loops do not run automatically
 *   full   — today's behaviour, always with an expiry (fullUntil)
 */

export const LIVE_INGEST_MODES = ["off", "ingest", "full"] as const;
export type LiveIngestMode = (typeof LIVE_INGEST_MODES)[number];

/** The jsonb value of app_settings.live_ingest_mode. */
export interface LiveIngestSetting {
  mode: LiveIngestMode;
  fullUntil: string | null;
}

export const LIVE_INGEST_MAX_FULL_MS = 24 * 60 * 60 * 1000;

const OFF: LiveIngestSetting = { mode: "off", fullUntil: null };

function isMode(v: unknown): v is LiveIngestMode {
  return typeof v === "string" && (LIVE_INGEST_MODES as readonly string[]).includes(v);
}

/** Malformed or missing → { mode: "off", fullUntil: null }. */
export function parseLiveIngestSetting(raw: unknown): LiveIngestSetting {
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) return { ...OFF };
  const { mode, fullUntil } = raw as Record<string, unknown>;
  if (!isMode(mode)) return { ...OFF };
  if (fullUntil !== undefined && fullUntil !== null && typeof fullUntil !== "string") return { ...OFF };
  return { mode, fullUntil: mode === "full" ? ((fullUntil as string | null | undefined) ?? null) : null };
}

/** full with fullUntil <= now, or full with no fullUntil → "ingest"; otherwise setting.mode. */
export function effectiveLiveIngestMode(setting: LiveIngestSetting, nowMs: number): LiveIngestMode {
  if (setting.mode !== "full") return setting.mode;
  const until = setting.fullUntil ? Date.parse(setting.fullUntil) : NaN;
  return Number.isFinite(until) && until > nowMs ? "full" : "ingest";
}

/** API body validation. */
export function validateLiveIngestInput(
  body: unknown,
  nowMs: number,
):
  | { ok: true; setting: LiveIngestSetting }
  | { ok: false; error: "invalid_mode" | "full_requires_fullUntil" | "fullUntil_invalid" } {
  const b = body !== null && typeof body === "object" ? (body as Record<string, unknown>) : {};
  if (!isMode(b.mode)) return { ok: false, error: "invalid_mode" };
  if (b.mode !== "full") return { ok: true, setting: { mode: b.mode, fullUntil: null } };

  if (b.fullUntil === undefined || b.fullUntil === null || b.fullUntil === "") {
    return { ok: false, error: "full_requires_fullUntil" };
  }
  if (typeof b.fullUntil !== "string" || !/^\d{4}-\d{2}-\d{2}T/.test(b.fullUntil)) {
    return { ok: false, error: "fullUntil_invalid" };
  }
  const until = Date.parse(b.fullUntil);
  if (!Number.isFinite(until) || until <= nowMs || until - nowMs > LIVE_INGEST_MAX_FULL_MS) {
    return { ok: false, error: "fullUntil_invalid" };
  }
  return { ok: true, setting: { mode: "full", fullUntil: new Date(until).toISOString() } };
}
